import React from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import axiosClient from '@/utils/client';
import {
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  Lock as LockIcon,
  MoreHoriz,
  VisibilityOff,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,

    Switch,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { File as FileIcon, FileImage, FilePdf } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FolderPlus } from '@untitled-ui/icons-react';

import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';

import type { CommunityFile } from '@/types/community';
import type { File } from '@/types/file';
import type { Folder } from '@/types/folder';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { CommunityPageHeader } from '@/components/common/community-page-header';
import { Seo } from '@/components/seo';

import ViewDocumentDialog from '../dashboard/dialogs/ViewDocument';
import { CreateFolderDialog } from './components/create-folder-modal';
import { DocumentFileSearchBox } from './components/document-file-search-box';
import { DocumentPreviewCard } from './components/document-preview-card';
import { EditFolderDialog } from './components/edit-folder-modal';
import { EditFileDialog } from './components/edit-file-modal';
import { UploadFileDialog } from './components/upload-file-modal';

interface MenuState {
  anchorEl: HTMLElement | null;
  item: any | null; // folder or file
}

export function DocumentsPage(): React.JSX.Element {
  const [folders, setFolders] = React.useState<Folder[]>([]);
  const [files, setFiles] = React.useState<File[]>([]); // New state for files
  const [searchResults, setSearchResults] = React.useState<{
    folders: Folder[];
    files: File[];
  }>({ folders: [], files: [] });
  const [isSearching, setIsSearching] = React.useState(false);
  const [selectedDocument, setSelectedDocument] = React.useState<CommunityFile | null>(null);
  const [menuState, setMenuState] = React.useState<MenuState>({ anchorEl: null, item: null });
  const [visibilityDialogOpen, setVisibilityDialogOpen] = React.useState(false);
  const [visibilityValue, setVisibilityValue] = React.useState<'public' | 'private'>('public');

  const folderDialog = useDialog();
  const fileDialog = useDialog();
  const viewDocumentDialog = useDialog();
  const editFolderDialog = useDialog();
  const editFileDialog = useDialog();
  const { user } = useUser();
  const navigate = useNavigate();


  const { data, refetch, error, isLoading } = useQuery({
    queryKey: ['folders'],
    queryFn: async () => {
      const response = await axiosClient.get('/api/v1/folders');
      return response.data;
    },
  });

  const createSearchRequest = useMutation({
    mutationFn: async ({ search }: { search: string }) => {
      const response = await axiosClient.get(`/api/v1/folders/search?query=${search}`);
      return response.data;
    },
    onSuccess: (data) => {
      setSearchResults({
        folders: data.folders || [],
        files: data.files || [],
      });
      setIsSearching(true);
    },
    onError: (error) => {
      console.error('Search failed:', error);
      // Add error handling/notification here
    },
  });
  React.useEffect(() => {
    if (data) {
      setFolders(data.folders || []);
      setFiles(data.files || []);
    }
  }, [data]);

  const handleSearch = React.useCallback(
    async (search: string) => {
      if (!search || search === '') {
        setIsSearching(false);
        setSearchResults({ folders: [], files: [] });
        await refetch();
        return;
      }
      await createSearchRequest.mutateAsync({ search });
    },
    [refetch, createSearchRequest]
  );

  const renderContent = () => {
    if (isLoading) {
      return <Typography variant="body1">Loading...</Typography>;
    }

    if (isSearching) {
      const hasResults = searchResults.folders.length > 0 || searchResults.files.length > 0;
      if (!hasResults) {
        return <Typography variant="body1">No results found</Typography>;
      }

      return (
        <>
          {searchResults.folders.length > 0 && (
            <>
              <Typography sx={{ mt: 2, mb: 1 }} variant="subtitle1">
                Folders
              </Typography>
              <List>
                {searchResults.folders.map((folder: Folder, index: number) => (
                  <React.Fragment key={folder.id}>
                    <ListItem>{renderDocumentOrFile(folder)}</ListItem>
                    {index !== searchResults.folders.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
          {searchResults.files.length > 0 && (
            <>
              <Typography sx={{ mt: 2, mb: 1 }} variant="subtitle1">
                Files
              </Typography>
              <List>
                {searchResults.files.map((file: any, index: number) => (
                  <React.Fragment key={file.id}>
                    <ListItem>{renderDocumentOrFile(file)}</ListItem>
                    {index !== searchResults.files.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              {selectedDocument ? <DocumentPreviewCard file={selectedDocument} /> : null}
            </>
          )}
        </>
      );
    }

    // Normal state (not searching)
    const hasContent = folders.length > 0 || files.length > 0;
    if (!hasContent) {
      return <Typography variant="body1">No documents found</Typography>;
    }

    return (
      <>
        {folders.length > 0 && (
          <>
            <Typography sx={{ mt: 2, mb: 1 }} variant="subtitle1">
              Folders
            </Typography>
            <List>
              {folders.map((folder: Folder, index: number) => (
                <React.Fragment key={folder.id || index}>
                  <ListItem>{renderDocumentOrFile(folder)}</ListItem>
                  {index !== folders.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </>
        )}
        {files.length > 0 && (
          <>
            <Typography sx={{ mt: 2, mb: 1 }} variant="subtitle1">
              Files
            </Typography>
            <List>
              {files.map((file: any, index: number) => (
                <React.Fragment key={file.id}>
                  <ListItem>{renderDocumentOrFile(file)}</ListItem>
                  {index !== files.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            {selectedDocument ? <DocumentPreviewCard file={selectedDocument} /> : null}
          </>
        )}
      </>
    );
  };

  const handleFolderCreate = async () => {
    console.log('handleFolderCreate');
    await refetch();
    folderDialog.handleClose();
  };

  const handleFileCreate = async () => {
    console.log('handleFileCreate');
    await refetch();
    fileDialog.handleClose();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: any) => {
    event.stopPropagation();
    // Set the menu state first so the dialogs have the correct item
    setMenuState({ anchorEl: null, item });
    
    // Then open the appropriate dialog based on item type
    if (item.type === 'folder') {
      editFolderDialog.handleOpen();
    } else if (item.type === 'file') {
      editFileDialog.handleOpen();
    }
  };

  const handleMenuClose = () => {
    setMenuState({ anchorEl: null, item: null });
    editFolderDialog.handleClose();
  };

  const clearMenuState = () => {
    setMenuState({ anchorEl: null, item: null });
  };

  const openVisibilityEditor = () => {
    if (!menuState.item) return;
    const current = (menuState.item).visibility === 'private' ? 'private' : 'public';
    setVisibilityValue(current);
    setVisibilityDialogOpen(true);
  };

  const handleSaveVisibility = async () => {
    if (!menuState.item) return;
    try {
              const isFolder = menuState.item.type === 'folder';
      if (isFolder) {
        await axiosClient.put(`/api/v1/folders/${menuState.item.id}`, { visibility: visibilityValue });
      } else {
        await axiosClient.put(`/api/v1/files/${menuState.item.id}`, { visibility: visibilityValue });
      }
      await refetch();
      setVisibilityDialogOpen(false);
      handleMenuClose();
    } catch (e) {
      console.error('Failed to update visibility', e);
    }
  };



  const handleMove = () => {
    // Implement move functionality
    handleMenuClose();
  };

  const handleEdit = React.useCallback(async () => {
    handleMenuClose();
    // Implement edit functionality
    await refetch();
  }, [refetch]);



  const renderFileTypeIcon = (item: File) => {
    const mimeType = item.mime_type;

    switch (mimeType) {
      case 'pdf':
        return <FilePdf />;
      case 'jpeg':
        return <FileImage />;
      default:
        return <FileIcon />;
    }
  };

  const renderDocumentIcon = (item: any) => {
    const isFolder = item.type === 'folder';
    const isPrivate = (item).visibility === 'private';

    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <Avatar>{isFolder ? <FolderIcon /> : renderFileTypeIcon(item as unknown as File)}</Avatar>
        {isPrivate ? <Box
            sx={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (theme) => theme.shadows[1],
            }}
          >
            <LockIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
          </Box> : null}
      </Box>
    );
  };

  const renderDocumentOrFile = (item: any) => {
    const isFolder = item.type === 'folder';
    const isPrivate = (item).visibility === 'private';

    return (
      <ListItemButton
        onClick={() => {
          if (!isFolder) {
            setSelectedDocument(item as unknown as CommunityFile);
            viewDocumentDialog.handleOpen();
            return;
          }
          navigate(paths.documents.folder(item.id));
        }}
      >
        <ListItemAvatar>{renderDocumentIcon(item)}</ListItemAvatar>
        <ListItemText
          primary={item.name}
          secondary={isFolder && item.item_count ? `${item.item_count} items` : ''}
        />

        <RoleGuard
          requiredAccountRoles={[
            ACCOUNT_ROLES.ACCOUNT_ADMIN,
            ACCOUNT_ROLES.ACCOUNT_MANAGER,
            ACCOUNT_ROLES.ACCOUNT_OWNER,
          ]}
          requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
        >
          <ListItemSecondaryAction>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                handleMenuOpen(event, item);
              }}
              size="small"
            >
              <MoreHoriz />
            </IconButton>
          </ListItemSecondaryAction>
        </RoleGuard>
      </ListItemButton>
    );
  };

  return (
    <>
      <Seo title="Documents" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          backgroundColor: (theme) => theme.palette.background.default,
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="xl">
          <CommunityPageHeader
            icon={<FileIcon size={36} />}
            subtitle="Browse, upload, and manage community files and folders"
            title="Documents"
          />
          <Stack spacing={3}>
            {user?.selected_community_id !== null ? (
              <Card>
                <CardHeader
                  action={
                    <Box gap={2} sx={{ display: 'flex', alignItems: 'center' }}>
                      <DocumentFileSearchBox loading={createSearchRequest.isPending} onSearch={handleSearch} />
                      <RoleGuard
                        requiredAccountRoles={[
                          ACCOUNT_ROLES.ACCOUNT_ADMIN,
                          ACCOUNT_ROLES.ACCOUNT_MANAGER,
                          ACCOUNT_ROLES.ACCOUNT_OWNER,
                        ]}
                        requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
                      >
                        <Button onClick={folderDialog.handleOpen} startIcon={<FolderPlus />} variant="contained">
                          Create Folder
                        </Button>
                        <Button
                          onClick={fileDialog.handleOpen}
                          startIcon={<CloudUploadOutlinedIcon />}
                          variant="contained"
                        >
                          Add Document
                        </Button>
                      </RoleGuard>
                    </Box>
                  }
                  avatar={
                    <Avatar>
                      <FolderIcon />
                    </Avatar>
                  }
                  title="Documents"
                />
                <CardContent>{renderContent()}</CardContent>
              </Card>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Alert severity="info">
                  <AlertTitle>Document Info</AlertTitle>
                  To view documents, you must be viewing as a single community.
                </Alert>
              </Box>
            )}
          </Stack>
        </Container>
      </Box>


        {/* Visibility Dialog */}
        <Dialog onClose={() => { setVisibilityDialogOpen(false); }} open={visibilityDialogOpen}>
          <DialogTitle>Edit Visibility</DialogTitle>
          <DialogContent>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={visibilityValue === 'private'}
                      onChange={(e) => { setVisibilityValue(e.target.checked ? 'private' : 'public'); }}
                    />
                  }
                  label={visibilityValue === 'private' ? 'Private' : 'Public'}
                />
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setVisibilityDialogOpen(false); }}>Cancel</Button>
            <Button onClick={handleSaveVisibility} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>
      <RoleGuard
        requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER, ACCOUNT_ROLES.ACCOUNT_OWNER]}
        requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
      >
        <CreateFolderDialog onClose={folderDialog.handleClose} onCreate={handleFolderCreate} open={folderDialog.open} />
        <UploadFileDialog onClose={fileDialog.handleClose} onCreate={handleFileCreate} open={fileDialog.open} />
      </RoleGuard>
      <ViewDocumentDialog
        file={selectedDocument}
        onClose={() => {
          setSelectedDocument(null);
          navigate(location.pathname);
          viewDocumentDialog.handleClose();
        }}
        open={viewDocumentDialog.open}
      />
      <RoleGuard
        requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER, ACCOUNT_ROLES.ACCOUNT_OWNER]}
        requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
      >
        <EditFolderDialog
          folder={menuState.item as unknown as Folder}
          onClose={() => {
            editFolderDialog.handleClose();
            // Don't clear menuState.item here, keep it for the dialog
          }}
          onEdit={handleEdit}
          onSuccess={handleFolderCreate}
          open={editFolderDialog.open}
        />
        <EditFileDialog
          file={menuState.item as unknown as File}
          onClose={() => {
            editFileDialog.handleClose();
            // Don't clear menuState.item here, keep it for the dialog
          }}
          onEdit={handleEdit}
          onSuccess={handleFolderCreate}
          open={editFileDialog.open}
        />
      </RoleGuard>
    </>
  );
}
